/* tslint:disable:max-line-length */
import {Injectable, OnDestroy} from '@angular/core';
import {AttachmentSyncService} from '@docma-mm-core/attachment/attachment-sync.service';
import {FaultOperationPathEnum} from '@docma-mm-core/fault/patch/fault-operation-path.enum';
import {FaultPatch} from '@docma-mm-core/fault/patch/fault-patch';
import {FaultPatchService} from '@docma-mm-core/fault/patch/fault-patch-service';
import {ChangeState} from '@docma-mm-core/model/change-state/change-state';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {SyncState} from '@docma-mm-core/model/sync-state/sync-state';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {ActivityData} from '../activity';
import {ActivityApiService} from '../activity-api.service';
import {ActivityDbService} from '../db/activity-db.service';
import {SyncResult} from '@docma-mm-core/model/sync-state/sync-result';
import {AttachmentData} from '@docma-mm-core/attachment/attachment';
import {User} from '@docma-mm-core/user/user';
import {ErrorService} from '../../../mm-faults/mm-error.service';
import {Logger} from '@mydocma/common/src/lib/logger';
import {ScribbleData} from '@docma-mm-core/scribble/scribble';

@Injectable()
export class ActivitySyncService implements OnDestroy {

  constructor(private readonly dbService: ActivityDbService,
              private readonly attachmentSyncService: AttachmentSyncService,
              private readonly apiService: ActivityApiService,
              private readonly patchService: FaultPatchService,
              private readonly errorService: ErrorService,
              private readonly logger: Logger) {
  }

  ngOnDestroy(): void {
  }

  public async syncActivitiesFromPatches(mydocmaUid: string, faultId: any, patches: FaultPatch[]): Promise<SyncResult> {

    let activityError: any;
    let activityLoadState;
    const attachmentErrors: any[] = [];
    const activityAttachmentsLoadStates: LoadState<AttachmentData>[] = [];
    const activityAttachmentScribblesLoadStates: LoadState<ScribbleData>[] = [];

    const faultUid = patches[0].fUid;
    const activityUid = (patches[0].operation.path == FaultOperationPathEnum.activity ? patches[0].oUid : patches[0].pUid);

    try {
      // console.debug(`Syc fault activity ${faultUid}/${activityUid}: Starting`);

      const activityPatches = patches.filter(p => p.operation.path === FaultOperationPathEnum.activity);
      await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, new SyncState({status: SyncStatusEnum.Syncing}));
      const activityChangeState = this.patchService.generateChangeStateFromPatches(activityPatches);
      // let hasError = false;
      try {
          if ((activityPatches?.length ?? 0) > 0) {
              activityLoadState = await this.syncActivityDataFromPatches(mydocmaUid, faultId, activityPatches);
          }
      } catch (err) {
        if (err.error === 'Unique constraint violation of \'UQ_A_UNIQUE_ID\'') {
          // ACTIVITY ALREADY UPLOADED, REMOVE FAULT_PATCH
          try {
            await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));

            try {
              const activityData = await this.apiService.getActivity(activityUid);
              const activityLoadedState = new LoadState<ActivityData>({status: LoadStatusEnum.Loaded, data: activityData});
              await this.dbService.saveLoadState(mydocmaUid, faultUid, activityUid, activityLoadedState);
            } catch (err3) {
              this.logger.error('Error loading activity data after unique constraint violation: ' + err3, '', 'ActivitySyncService');
            }

          } catch (err2) {
            activityError = this.errorService.getError(err2);
          }
        } else {
          activityError = this.errorService.getError(err);
        }
      }

      const groupedAttachmentPatches = this.patchService.groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachment));
      const groupedActivityAttachmentScribblePatches = this.patchService.groupByPuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityAttachmentScribbles));

      for (const item of Object.entries(groupedActivityAttachmentScribblePatches)) {
        // concat scribble patches with activity attachment patches
        groupedAttachmentPatches[item[0]] = (groupedAttachmentPatches[item[0]] ?? []).concat(item[1]);
      }

      let modifiedOn: Date;
      let modifier: User;

      for (const item of Object.values(groupedAttachmentPatches)) {
        const result = await this.attachmentSyncService.syncActivityAttachmentFromPatches(mydocmaUid, (activityLoadState?.data.Id ?? activityLoadState?.data.UniqueId), item);
        if (result.isNewAttachment && result.syncResult.syncState.status === SyncStatusEnum.Synced && (!modifiedOn || modifiedOn < new Date(result.syncResult.activityAttachmentsLoadStates[0].data.ModifiedOn))) {
          modifiedOn = result.syncResult.activityAttachmentsLoadStates[0].data.ModifiedOn;
          modifier = result.syncResult.activityAttachmentsLoadStates[0].data.Modifier;
        }

        if (result.syncResult.syncState.status === SyncStatusEnum.Error) {
          attachmentErrors.push(result.syncResult.syncState.error);
        } else {
          activityAttachmentsLoadStates.push(...result.syncResult.activityAttachmentsLoadStates);
          activityAttachmentScribblesLoadStates.push(...result.syncResult.activityAttachmentScribblesLoadStates);
        }
      }

      if (modifiedOn && modifier) {
        await this.dbService.saveLoadStateModified(mydocmaUid, faultUid, activityUid, modifiedOn, modifier);
      }


      // const groupedAttachment2Patches = this.patchService
      // .groupByOuid(patches.filter(p => p.operation.path === FaultOperationPathEnum.activityGroupAttachment));

      // for (const item of Object.values(groupedAttachment2Patches)) {
      //   await this.attachmentSyncService.syncActivityGroupAttachmentFromPatches(mydocmaUid, activityLoadState?.data.Group.Id, item);
      // }

      if (activityError) {
        const syncStateActivityErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, new SyncState({status: SyncStatusEnum.Error, error: activityError}));
        return new SyncResult({syncState: syncStateActivityErr});
      }
      else if (attachmentErrors.length > 0) {
        const syncStateAttachmentErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, new SyncState({status: SyncStatusEnum.Error, error: attachmentErrors[0]}));
        return new SyncResult({syncState: syncStateAttachmentErr});
      }

      const syncState = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, new SyncState({status: SyncStatusEnum.Synced}));
      return new SyncResult({syncState, activityLoadStates: [activityLoadState], activityAttachmentsLoadStates, activityAttachmentScribblesLoadStates });
    }
    catch (err) {
      const syncStateErr = await this.dbService.saveSyncState(mydocmaUid, faultUid, activityUid, new SyncState({status: SyncStatusEnum.Error, error: this.errorService.getError(err)}));
      return new SyncResult({syncState: syncStateErr});
    }
  }

  private async syncActivityDataFromPatches(mydocmaUid: string, faultId: any, patches: FaultPatch[]): Promise<LoadState<ActivityData>> {
    this.assertValidPatches(patches, FaultOperationPathEnum.activity);
    let loadedState: LoadState<ActivityData>;
    const activityUid = patches[0].oUid;
    const faultUid = patches[0].fUid;
    const activityChangeState = this.patchService.generateChangeStateFromPatches(patches);
    if (activityChangeState.status !== ChangeStatusEnum.NotChanged) {
      loadedState = await this.syncActivityChangeState(mydocmaUid, faultId ?? patches[0].pId, patches[0].oId, activityChangeState);
      await this.dbService.saveLoadState(mydocmaUid, faultUid, activityUid, loadedState);
    }
    await this.patchService.remove(mydocmaUid, patches.map(p => p.operation.id));
    return loadedState;

  }

  private async syncActivityChangeState(mydocmaUid: string, faultId: number, activityId: any,
    changeState: ChangeState<ActivityData>): Promise<LoadState<ActivityData>> {
    if (changeState.status === ChangeStatusEnum.New) {
      const loadedData = await this.apiService.createFaultActivity(faultId, changeState.data).toPromise();
      return new LoadState<ActivityData>({ status: LoadStatusEnum.Loaded, data: loadedData });
    } else if (changeState.status === ChangeStatusEnum.Changed) {
      const updatedData = await this.apiService.updateActivity(activityId, changeState.data).toPromise();
      return new LoadState<ActivityData>({ status: LoadStatusEnum.Loaded, data: updatedData });

    }
  }

  private assertValidPatches(patches: FaultPatch[], path: FaultOperationPathEnum) {

    const faultUid = patches[0].fUid;
    const oUid = patches[0].oUid;
    for (const p of patches) {
      if (p.fUid !== faultUid || p.oUid !== oUid || p.operation.path !== path) {
        throw new Error(`Can't sync activity ${faultUid}/${oUid}. Sync patches not valid`);
      }
    }
  }

}
